import React from 'react'
import styled from '@emotion/styled'
import { colors, font, radius } from '@mobi/component-library/Theme/Common'
import type { BetLoopStats } from '@core/Areas/BetLoop/types'

export const Last10WinLossIndicator: React.FC<{
  last10Results: BetLoopStats['lastTenResults']
  currentStreak: number
  size?: 'S' | 'L'
}> = ({ last10Results, currentStreak, size = 'S' }) => {
  const isStreakActive = currentStreak >= 3
  const highlightCount = isStreakActive ? Math.min(currentStreak, last10Results.length) : 0

  return last10Results.map((status, i) => {
    const isWin = status === 'Won'
    const isLoss = status === 'NoPayout'
    const isInStreak = isStreakActive && i >= last10Results.length - highlightCount

    const classNames = ['last10-indicator']
    if (isLoss) classNames.push('last10-indicator--loss')
    if (isWin) {
      if (isInStreak) classNames.push('last10-indicator--streak')
      else classNames.push('last10-indicator--win')
    }

    // Using '-' for non win/loss fixes share image creation on iOS
    const displayIndicator = isWin ? 'W' : isLoss ? 'L' : '-'

    return (
      <WrapperStyled
        key={i}
        data-testid='last10-win-loss-indicator'
        className={classNames.join(' ')}
        data-last10-size={size}
        data-last10-value={displayIndicator}
      >
        {displayIndicator}
      </WrapperStyled>
    )
  })
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  flexShrink: 0,
  position: 'relative',
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '2rem',
  height: '2rem',
  fontFamily: font.family.primary,
  fontWeight: font.weight.black,
  fontSize: '1rem',
  fontStyle: 'italic',
  lineHeight: 1,
  borderRadius: radius.full,
  backgroundColor: '#DFDFDF',
  color: '#DFDFDF',

  '&[data-last10-size="L"]': {
    width: '2.8rem',
    height: '2.8rem',
    fontSize: '1.5rem',
  },

  '::after, ::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: radius.full,
  },

  '::after': {
    border: '1px solid transparent',
    zIndex: 2,
  },

  '&.last10-indicator': {
    '&--win': {
      backgroundColor: '#27BB36',
      color: colors.success[500],
      boxShadow: '0 1.35px 1.75px 0 #58AB62, 0 7.5px 7.5px 0 rgba(255, 255, 255, 0.50) inset',

      '&[data-last10-size="S"]': {
        boxShadow:
          'rgb(88, 171, 98) 0px 1px 1px 0.5px, rgba(255, 255, 255, 0.5) 0px 5px 5px 0px inset',
      },

      '::after': { borderColor: 'rgba(255, 255, 255, 0.18)' },
    },

    '&--loss': {
      backgroundColor: '#D9D9D9',
      color: colors.neutral[700],
      boxShadow:
        '0 1.35px 1.75px 0 rgba(0, 0, 0, 0.28), 0 6px 6px 0 rgba(255, 255, 255, 0.90) inset',

      '::after': { borderColor: 'rgba(231, 231, 231, 0.40)' },
    },

    '&--streak': {
      color: colors.success[500],

      '::before': {
        margin: '-1px',
        background:
          'linear-gradient(to bottom, #FFD687 0%, #FF9D57 35%, #FF894B 70%, #FF5635 100%)',
        boxShadow: '0 1px 1.75px 0 rgba(255, 106, 59, 0.60)',
      },

      '::after': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        content: 'attr(data-last10-value)',
        boxSizing: 'border-box',
        border: '1px solid white',
        margin: '1px',
        backgroundColor: '#27BB36',
        boxShadow: '0 5px 5px 0 rgba(255, 255, 255, 0.50) inset',
      },
    },
  },
})
